import type { Location, RouteInfo } from '@/types';

/**
 * Routing service for calculating routes and distances
 * Uses OpenRouteService API for route calculations
 */
export class RoutingService {
  private static readonly ORS_API_KEY = (import.meta as any).env?.VITE_ORS_API_KEY;
  private static readonly ORS_BASE_URL = 'https://api.openrouteservice.org/v2';

  /**
   * Calculate route between two points
   * Falls back to straight-line distance if routing API is unavailable
   */
  static async calculateRoute(
    from: Location,
    to: Location,
    profile: 'driving-car' | 'cycling-regular' | 'foot-walking' = 'driving-car'
  ): Promise<RouteInfo> {
    // If no API key is configured, fall back to straight-line distance
    if (!this.ORS_API_KEY) {
      return this.calculateStraightLineRoute(from, to);
    }

    try {
      const response = await fetch(`${this.ORS_BASE_URL}/directions/${profile}`, {
        method: 'POST',
        headers: {
          'Authorization': this.ORS_API_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          coordinates: [
            [from.longitude, from.latitude],
            [to.longitude, to.latitude],
          ],
          format: 'json',
        }),
      });

      if (!response.ok) {
        throw new Error('Routing API request failed');
      }

      const data = await response.json();
      const route = data.routes[0];

      return {
        distance: this.metersToMiles(route.summary.distance),
        duration: route.summary.duration / 60, // Convert seconds to minutes
        coordinates: route.geometry?.coordinates?.map(([lng, lat]: [number, number]) => [lat, lng]),
      };
    } catch (error) {
      console.warn('Routing API failed, falling back to straight-line distance:', error);
      return this.calculateStraightLineRoute(from, to);
    }
  }

  /**
   * Calculate straight-line distance and estimated travel time
   */
  private static calculateStraightLineRoute(from: Location, to: Location): RouteInfo {
    const distance = this.calculateDistance(from, to);
    
    // Estimate travel time based on average speeds
    // Road distance is typically ~1.3x straight-line distance
    const roadDistance = distance * 1.3;
    const averageSpeed = 30; // mph for mixed driving
    const duration = (roadDistance / averageSpeed) * 60; // Convert to minutes

    return {
      distance: roadDistance,
      duration,
    };
  }

  /**
   * Calculate distance between two points using Haversine formula
   * Returns distance in miles
   */
  private static calculateDistance(point1: Location, point2: Location): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRadians(point2.latitude - point1.latitude);
    const dLon = this.toRadians(point2.longitude - point1.longitude);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(point1.latitude)) * Math.cos(this.toRadians(point2.latitude)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  private static toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Convert meters to miles
   */
  private static metersToMiles(meters: number): number {
    return meters * 0.000621371;
  }

  /**
   * Batch calculate routes for multiple destinations
   */
  static async calculateMultipleRoutes(
    from: Location,
    destinations: Location[],
    profile: 'driving-car' | 'cycling-regular' | 'foot-walking' = 'driving-car'
  ): Promise<RouteInfo[]> {
    const promises = destinations.map(destination => 
      this.calculateRoute(from, destination, profile)
    );

    try {
      return await Promise.all(promises);
    } catch (error) {
      console.error('Failed to calculate multiple routes:', error);
      // Return straight-line calculations for all destinations
      return destinations.map(destination => 
        this.calculateStraightLineRoute(from, destination)
      );
    }
  }

  /**
   * Get isochrone (travel time polygon) - simplified implementation
   * In a real app, you'd use ORS isochrones API
   */
  static calculateIsochrone(
    center: Location,
    travelTimeMinutes: number,
    profile: 'driving-car' | 'cycling-regular' | 'foot-walking' = 'driving-car'
  ): Location[] {
    // Simplified circular approximation
    // In reality, you'd call ORS isochrones API
    const speedKmh = profile === 'driving-car' ? 50 : profile === 'cycling-regular' ? 15 : 5;
    const radiusKm = (speedKmh * travelTimeMinutes) / 60;
    const radiusDegrees = radiusKm / 111.32; // Rough conversion

    const points: Location[] = [];
    const segments = 32;
    
    for (let i = 0; i < segments; i++) {
      const angle = (i * 2 * Math.PI) / segments;
      points.push({
        latitude: center.latitude + radiusDegrees * Math.cos(angle),
        longitude: center.longitude + radiusDegrees * Math.sin(angle),
      });
    }

    return points;
  }
}